import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useCreateKennenlernen } from '../../api/kennenlernen';
import type { CreateKennenlernenDto } from '../../types/kennenlernen';

interface CreateKennenlernenModalProps {
  isOpen: boolean;
  onClose: () => void;
  bedarfId: string;
  bewerberId: string;
  bewerberName: string;
  childName: string;
}

export function CreateKennenlernenModal({
  isOpen,
  onClose,
  bedarfId,
  bewerberId,
  bewerberName,
  childName,
}: CreateKennenlernenModalProps) {
  const createMutation = useCreateKennenlernen();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Omit<CreateKennenlernenDto, 'bedarfId' | 'bewerberId'>>({
    defaultValues: {
      scheduledAt: '',
      location: '',
      notes: '',
    },
  });

  const handleFormSubmit = async (data: Omit<CreateKennenlernenDto, 'bedarfId' | 'bewerberId'>) => {
    setError(null);
    try {
      await createMutation.mutateAsync({
        bedarfId,
        bewerberId,
        scheduledAt: new Date(data.scheduledAt).toISOString(),
        location: data.location || undefined,
        notes: data.notes || undefined,
        sendEmails: true, // Send invitation emails to Bewerber and parent
      });
      toast.success('Kennenlernen erfolgreich geplant und Einladungen versendet');
      reset();
      onClose();
    } catch (err) {
      setError('Fehler beim Erstellen des Kennenlernens');
      toast.error('Fehler beim Erstellen des Kennenlernens');
      console.error('Failed to create kennenlernen:', err);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Kennenlernen planen
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Kennenlernen zwischen <span className="font-medium">{bewerberName}</span> und <span className="font-medium">{childName}</span> planen.
                  </p>

                  {error && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div className="mt-4 space-y-4">
                    <div>
                      <label htmlFor="scheduledAt" className="block text-sm font-medium text-gray-700">
                        Termin *
                      </label>
                      <input
                        type="datetime-local"
                        id="scheduledAt"
                        {...register('scheduledAt', { required: 'Termin ist erforderlich' })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                      {errors.scheduledAt && (
                        <p className="mt-1 text-sm text-red-600">{errors.scheduledAt.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                        Ort
                      </label>
                      <input
                        type="text"
                        id="location"
                        {...register('location')}
                        placeholder="z.B. Schule, Büro..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notizen
                      </label>
                      <textarea
                        id="notes"
                        rows={2}
                        {...register('notes')}
                        placeholder="Zusätzliche Informationen..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {createMutation.isPending ? 'Wird erstellt...' : 'Kennenlernen planen'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
